/**
 * TimestampBadge.tsx — ختم الوقت والتاريخ أعلى الخريطة، اختياريّ ومطفأ افتراضياً.
 *
 * ── لماذا وُجد (طلب العميل 2026-09-16) ──────────────────────────────────────
 * الناس يلتقطون صورة الشاشة وينشرونها: «انظروا المنخفض قادم». والصورة المنشورة تفقد
 * كل ما يجعلها **دليلاً**: الشريط الزمني أسفل الشاشة يُقصّ أو يُخفى، فتصير لقطةُ توقّعٍ
 * بلا تاريخ ولا ساعة — لا يعرف قارئها أهي لليوم أم لما بعد ثلاثة أيام، ولا متى
 * التُقطت أصلاً. والتطبيقات المرجعية تضع لهذا خَتماً أعلى الخريطة تماماً.
 *
 * فالفائدة ليست تكراراً للشريط: الشريط أداةُ تحكّمٍ تعيش في الصفحة، والختم **يسافر
 * مع الصورة**.
 *
 * ── ولماذا مطفأ افتراضياً ───────────────────────────────────────────────────
 * لأن الشريط يقول الشيء نفسه لمن هو أمام الشاشة، وإضافةُ صندوقٍ دائم فوق الخريطة
 * تأخذ من مساحة الطقس بلا مقابل لأكثر الزوّار. فمن يصوّر للنشر يُشغّله من قائمة
 * التراكبات، ويبقى مشتغلاً له وحده (يُحفظ في المتجر).
 *
 * ── سطران لا واحد ───────────────────────────────────────────────────────────
 *   • **زمن العرض**: اللحظة التي تعرضها الخريطة الآن — وهي المعلومة التي تُصحّح فهم
 *     من يرى الصورة («هذه ليلة الجمعة لا الآن»).
 *   • **آخر تحديث**: متى وصل أحدث رصدٍ أو دورة نموذج. بلا هذا السطر تبقى الصورة
 *     صادقةً عن اللحظة المعروضة وصامتةً عن حداثة مصدرها — وهما سؤالان مختلفان.
 */

import { memo, useLayoutEffect, useRef } from 'react';
import { useWeatherStore } from '../../store/weatherStore';
import { useTranslation } from '../../i18n/LanguageContext';
import { MOBILE_LEGEND_STRIP_H } from './CentralLegend';

interface Props {
    /** اللحظة المعروضة على الشريط (ثوانٍ). */
    displayEpoch: number;
    /** أحدث رصدٍ متاح أو زمن دورة النموذج (ثوانٍ)، أو null إن لم يُعرف. */
    updatedEpoch: number | null;
    /** هل يشغل مفتاح الألوان شريط أعلى الشاشة على الجوّال؟ */
    legendPresent: boolean;
}

function fmt(epoch: number, locale: string, opts: Intl.DateTimeFormatOptions): string {
    return new Intl.DateTimeFormat(locale, opts).format(new Date(epoch * 1000));
}

export const TimestampBadge = memo(function TimestampBadge(
    { displayEpoch, updatedEpoch, legendPresent }: Props,
) {
    const on = useWeatherStore((s) => s.timestampBadgeEnabled);
    const { locale, t } = useTranslation();
    const box = useRef<HTMLDivElement | null>(null);

    /*
     * ينشر حافّته السفلى في `--stamp-bottom` لتقف تحتها قراءةُ علامة التصويب.
     *
     * ── لماذا بالقياس لا برقم ─────────────────────────────────────────────
     * موضع الختم يتغيّر: شريط مفتاح الألوان يزيحه 30 بكسل على الجوّال، وارتفاعه نفسه
     * يتغيّر بوجود سطر «آخر تحديث» من عدمه، وبالخطّ وحجمه. فأيّ إزاحة ثابتة تصحّ في
     * حالة وتُخطئ في البواقي — وهو ما وقع: 50 بكسل أبعدت القراءة كثيراً في حالة
     * وكانت ستُلاصقها في أخرى.
     */
    useLayoutEffect(() => {
        const root = document.documentElement;
        const publish = () => {
            const el = box.current;
            if (!on || !el) { root.style.removeProperty('--stamp-bottom'); return; }
            root.style.setProperty('--stamp-bottom',
                `${Math.round(el.getBoundingClientRect().bottom)}px`);
        };
        publish();
        // تدوير الشاشة لا يُعيد التصيير بالضرورة (الشريط قد يكون واقفاً على إطار)،
        // وحينها يبقى الرقم المنشور من الوضع السابق فتُزاح القراءة خطأً.
        window.addEventListener('resize', publish);
        return () => {
            window.removeEventListener('resize', publish);
            root.style.removeProperty('--stamp-bottom');
        };
    });
    if (!on || !Number.isFinite(displayEpoch)) return null;

    const day = fmt(displayEpoch, locale, { weekday: 'long', day: 'numeric', month: 'long' });
    const time = fmt(displayEpoch, locale, { hour: 'numeric', minute: '2-digit' });
    const updated = updatedEpoch !== null && Number.isFinite(updatedEpoch)
        ? fmt(updatedEpoch, locale, { hour: 'numeric', minute: '2-digit' })
        : null;

    return (
        <div
            ref={box}
            // ‏pointer-events-none: ختمٌ يُقرأ ولا يُنقر — وإلّا ابتلع نقرة على الخريطة
            // تحته، وهي أكثر ما يفعله المستخدم في هذا الموضع بالذات.
            // على الجوّال ينزل تحت الشعار: العرض 375 بكسل، والشعار أعلى اليسار والختم
            // في المنتصف — فيتقاطعان. (رُصد في المعاينة قبل النشر.) وعلى الشاشات
            // الأوسع يبقى في أعلى المنتصف حيث لا شيء يزاحمه.
            className="absolute left-1/2 -translate-x-1/2 z-[560] pointer-events-none select-none
                       top-3 max-[768px]:top-[var(--stamp-top-mobile)]
                       px-3.5 py-1.5 rounded-full text-center
                       bg-[rgba(15,23,42,0.82)] light:bg-[var(--panel-bg)]
                       border border-white/10 light:border-[var(--panel-border)]
                       backdrop-blur-[10px] shadow-[0_6px_24px_rgba(0,0,0,0.45)]
                       animate-fade-in"
            // شريط المفتاح يحتلّ أعلى الشاشة على الجوّال وحده، فيُزاح الختم بمقداره هناك.
            style={{ ['--stamp-top-mobile' as string]: `${(legendPresent ? MOBILE_LEGEND_STRIP_H : 0) + 56}px` }}
        >
            <div className="text-[13px] font-bold leading-tight text-white light:text-[var(--text-primary)] whitespace-nowrap">
                {day} · {time}
            </div>
            {updated && (
                <div className="text-[10.5px] leading-tight opacity-65 whitespace-nowrap">
                    {t('stamp.updated')} {updated}
                </div>
            )}
        </div>
    );
});
